/**
 * Chord strip (SPEC §6.3): four cells, one per bar, showing `numeral · name`
 * from `session.progression`. A cell gets a teal border when its slot was
 * written by the agent (the chords track holds agent notes in that bar), "?"
 * when the track has notes but no progression metadata (hand-edited), "—"
 * when empty. Click → inline text input → Enter parses through
 * `controller.setChordAt`; Escape cancels; errors are shown inline.
 */
import type { Store } from '../state/store-api';
import type { Controller } from '../app/controller';
import type { Session } from '../types';
import { BARS, STEPS_PER_BAR } from '../types';
import { h, injectStyle, setFlag } from './dom';

/** Dependencies for `mountChordStrip()`; only `setChordAt` is needed from the controller. */
export interface ChordStripDeps {
  store: Store;
  controller: Pick<Controller, 'setChordAt'>;
}

/** Mounted chord strip: root element, programmatic edit, dispose. */
export interface ChordStripHandle {
  el: HTMLElement;
  /** Open the inline editor for a 0-based bar. */
  edit(bar: number): void;
  dispose(): void;
}

const CSS = `
.bm-chords{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.5rem;min-width:0}
.bm-chord{position:relative;display:flex;flex-direction:column;align-items:flex-start;gap:.1rem;min-height:3.4rem;padding:.4rem .6rem;text-align:left;
  border:1px solid var(--border);border-radius:var(--radius-m);background:var(--surface-1);color:var(--fg);cursor:text;min-width:0;width:100%;
  transition:border-color .15s var(--ease),box-shadow .15s var(--ease)}
.bm-chord:hover{border-color:var(--fg-faint)}
.bm-chord[data-by="agent"]{border-color:var(--agent);box-shadow:inset 0 0 0 1px var(--agent-soft)}
.bm-chord[data-by="human"]{border-color:var(--human)}
.bm-chord[data-empty]{color:var(--fg-faint)}
.bm-chord__bar{font-size:.65rem;letter-spacing:.06em;text-transform:uppercase;color:var(--fg-muted)}
.bm-chord__main{display:flex;align-items:baseline;gap:.35rem;min-width:0;max-width:100%}
.bm-chord__numeral{font-weight:700;font-size:1.05rem}
.bm-chord__name{color:var(--fg-muted);font-size:.85rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bm-chord__edit{display:flex;flex-direction:column;gap:.25rem;min-height:3.4rem;padding:.35rem .45rem;border:1px solid var(--accent);border-radius:var(--radius-m);background:var(--surface-2);min-width:0}
.bm-chord__input{width:100%;min-width:0;font:inherit;font-weight:600;padding:.2rem .4rem;border:1px solid var(--border-strong);border-radius:var(--radius-s);background:var(--surface-1);color:var(--fg)}
.bm-chord__error{font-size:.72rem;color:var(--danger);line-height:1.25;overflow-wrap:anywhere}
.bm-chord__hint{font-size:.65rem;color:var(--fg-faint)}
`;

interface CellView {
  numeral: string;
  name: string | null;
  by: 'agent' | 'human' | null;
  empty: boolean;
}

/** What a chord cell should show for `bar`, derived from the session. */
export function chordCellView(s: Session, bar: number): CellView {
  const lo = bar * STEPS_PER_BAR;
  const hi = lo + STEPS_PER_BAR;
  const notes = s.tracks.chords.notes.filter((n) => n.step >= lo && n.step < hi);
  const slot = s.progression?.find((x) => x.bar === bar) ?? null;
  if (slot) {
    const agent = notes.some((n) => n.by === 'agent');
    const human = notes.some((n) => n.by === 'human');
    return { numeral: slot.numeral, name: slot.name, by: agent ? 'agent' : human ? 'human' : null, empty: false };
  }
  if (notes.length) return { numeral: '?', name: null, by: null, empty: false };
  return { numeral: '—', name: null, by: null, empty: true };
}

/** Mount the chord strip into `container`. */
export function mountChordStrip(container: HTMLElement, deps: ChordStripDeps): ChordStripHandle {
  injectStyle('bm-chords-css', CSS);
  const { store, controller } = deps;
  const el = h('div', { class: 'bm-chords', role: 'group', 'aria-label': 'Chord progression, one chord per bar' });
  const cells: HTMLButtonElement[] = [];
  const parts: Array<{ numeral: HTMLElement; name: HTMLElement }> = [];
  let editing: { bar: number; wrap: HTMLElement; input: HTMLInputElement; error: HTMLElement; submitting: boolean } | null = null;

  // DOM built once per cell; renders only flip text and attributes.
  const renderCell = (bar: number, s: Session) => {
    const v = chordCellView(s, bar);
    const cell = cells[bar]!;
    const part = parts[bar]!;
    if (part.numeral.textContent !== v.numeral) part.numeral.textContent = v.numeral;
    const nameText = v.name ? `· ${v.name}` : '';
    if (part.name.textContent !== nameText) part.name.textContent = nameText;
    part.name.hidden = !v.name;
    if (v.by) cell.dataset.by = v.by;
    else delete cell.dataset.by;
    setFlag(cell, 'data-empty', v.empty);
    const desc = v.empty ? 'empty' : v.name ? `${v.numeral} (${v.name})` : v.numeral === '?' ? 'hand-edited notes' : v.numeral;
    cell.setAttribute('aria-label', `Bar ${bar + 1} chord: ${desc}${v.by === 'agent' ? ', written by the agent' : ''}. Click to edit.`);
  };

  const closeEditor = () => {
    if (!editing) return;
    const { bar, wrap } = editing;
    editing = null;
    wrap.replaceWith(cells[bar]!);
    renderCell(bar, store.get());
  };

  const openEditor = (bar: number) => {
    if (editing?.bar === bar) return;
    closeEditor();
    const current = chordCellView(store.get(), bar);
    const input = h('input', {
      class: 'bm-chord__input',
      type: 'text',
      value: current.empty || current.numeral === '?' ? '' : current.numeral,
      placeholder: 'i, VI, V7, Am, Fmaj7',
      'aria-label': `Chord for bar ${bar + 1}`,
      autocomplete: 'off',
      spellcheck: 'false',
      'data-bar-input': bar,
    }) as HTMLInputElement;
    const error = h('div', { class: 'bm-chord__error', role: 'alert', hidden: true });
    const wrap = h(
      'form',
      { class: 'bm-chord__edit', dataset: { bar: String(bar), editing: 'true' }, novalidate: true },
      input,
      error,
      h('span', { class: 'bm-chord__hint' }, 'Enter to set · Esc to cancel'),
    );
    const state = { bar, wrap, input, error, submitting: false };
    editing = state;
    cells[bar]!.replaceWith(wrap);
    const submit = () => {
      if (editing !== state) return;
      state.submitting = true;
      const r = controller.setChordAt(bar, input.value);
      state.submitting = false;
      if (r.ok) {
        closeEditor();
        cells[bar]!.focus();
      } else {
        error.textContent = r.error;
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.select();
      }
    };
    // Implicit submission (Enter in a text input) covers browsers/automation that skip keydown.
    wrap.addEventListener('submit', (e) => {
      e.preventDefault();
      submit();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === 'Return' || e.code === 'Enter' || e.code === 'NumpadEnter') {
        e.preventDefault();
        e.stopPropagation();
        submit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeEditor();
        cells[bar]!.focus();
      } else {
        e.stopPropagation(); // typing must not reach the global shortcuts / piano
      }
    });
    input.addEventListener('keyup', (e) => e.stopPropagation());
    input.addEventListener('blur', () => {
      if (editing === state && !state.submitting) closeEditor();
    });
    input.focus();
    input.select();
  };

  for (let bar = 0; bar < BARS; bar++) {
    const numeral = h('span', { class: 'bm-chord__numeral' });
    const name = h('span', { class: 'bm-chord__name', hidden: true });
    const cell = h(
      'button',
      { type: 'button', class: 'bm-chord', dataset: { bar: String(bar) } },
      h('span', { class: 'bm-chord__bar' }, `Bar ${bar + 1}`),
      h('span', { class: 'bm-chord__main' }, numeral, name),
    ) as HTMLButtonElement;
    cell.addEventListener('click', () => openEditor(bar));
    cells.push(cell);
    parts.push({ numeral, name });
    el.append(cell);
  }
  const renderAll = (s: Session) => {
    for (let bar = 0; bar < BARS; bar++) if (editing?.bar !== bar) renderCell(bar, s);
  };
  renderAll(store.get());
  const unsub = store.subscribe((s) => renderAll(s));

  container.append(el);
  return {
    el,
    edit: openEditor,
    dispose() {
      unsub();
      closeEditor();
      el.remove();
    },
  };
}
